/**
 * @documental
 * @export Date
 * @name
 * Table.Date
 */
import { FC } from "react";

import { DateTime } from "luxon";

import { BaseColumnProps } from "../BaseColumnProps";
import { ColumnElementConstructor } from "../ColumnElementConstructor";
import { Custom } from "./Custom";

/**
 * The `Date` component properties.
 */
export interface DateProps extends Omit<BaseColumnProps, "align"> {
  /**
   * The date/time format. If not specified defaults to `"yyyy-LL-dd hh:mm:ss a"`. For more information on supported formats,
   * see the documentation for the <a href="https://moment.github.io/luxon/#/?id=luxon">Luxon</a> library.
   */
  readonly format?: string;

  /**
   * The timezone the value is associated with. If not specified defaults to `"utc"`.
   * When set to `"utc"`, the time portion of dates is normalized as being at midnight UTC.
   * When set to `"local"`, the time portion of dates is normalized as being at midnight in the current user's timezone offset.
   */
  readonly zone?: "utc" | "local";
}

/**
 * Column which displays a formatted date from either a Javascript Date object or a string which can be parsed into a date.
 */
export const Date: FC<DateProps> = (props) => {
  const { format = "yyyy-LL-dd hh:mm:ss a", zone = "utc" } = props;

  return (
    <Custom {...props}>
      {(value) => {
        if (value === null) return "--";
        if (value === undefined) return "--";
        if (value instanceof global.Date) {
          return DateTime.fromJSDate(value, { zone: zone }).toFormat(format);
        }
        if (typeof value === "string" && value.trim()) {
          return DateTime.fromISO(value, { zone: zone }).toFormat(format);
        }

        return "--";
      }}
    </Custom>
  );
};

(Date as ColumnElementConstructor<DateProps>).className = "date";
(Date as ColumnElementConstructor<DateProps>).headerAlignment = "left";
